/**
 * Address autocomplete via business-service's `geocodeSuggest` (MapTiler behind
 * it, key stays server-side). Used by onboarding to prefill the address from a
 * typed street or postal/zip. The admin talks only to its own backends — never
 * to the consumer discovery-service.
 */
import i18n from '../../i18n';

const API_URL = process.env.REACT_APP_BUSINESS_SERVICE_BASE_URL || '';

export interface GeoSuggestion {
    label: string;
    context?: string | null;
    houseNumber?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    region?: string | null;
    postcode?: string | null;
    /** Exact coordinates of the picked place — persisted so discovery skips re-geocoding. */
    latitude?: number | null;
    longitude?: number | null;
}

/** Autocomplete suggestions for `query`, biased to `country` (ISO alpha-2, e.g.
 *  'BR'/'CA'). Fail-soft to []: a geocoding miss must never block onboarding. */
export async function geocodeSuggest(query: string, country: string): Promise<GeoSuggestion[]> {
    if (!API_URL || !query || query.trim().length < 3) return [];
    try {
        const res = await fetch(API_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept-Language': i18n.language || 'pt' },
            body: JSON.stringify({
                query: `query($q: String!, $c: String) {
                    geocodeSuggest(query: $q, country: $c) {
                        label context houseNumber neighborhood city region postcode latitude longitude
                    }
                }`,
                variables: { q: query, c: country || null },
            }),
        });
        if (!res.ok) return [];
        const json = await res.json();
        if (json.errors) return [];
        return (json.data?.geocodeSuggest ?? []) as GeoSuggestion[];
    } catch {
        return [];
    }
}
